import React from 'react';
import { Button } from './ui/button';
import { CheckCircle, Clock, Mail, Calendar, Euro, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';

const PaymentSuccessModal = ({ registration, event, onClose, status = 'paid', bankInfo = null }) => {
  const isPending = status === 'pending';
  const { currentLanguage } = useLanguage();
  
  // Traductions
  const { translatedText: paymentPendingTranslated } = useTranslate("Paiement en attente");
  const { translatedText: paymentConfirmedTranslated } = useTranslate("Paiement confirmé !");
  const { translatedText: registrationPendingTranslated } = useTranslate("Votre inscription est enregistrée en attente de paiement.");
  const { translatedText: registrationConfirmedMsgTranslated } = useTranslate("Votre inscription à l'événement est confirmée.");
  const { translatedText: eventTranslated } = useTranslate("Événement");
  const { translatedText: ticketsReservedTranslated } = useTranslate("Billets réservés :");
  const { translatedText: totalTranslated } = useTranslate("Total");
  const { translatedText: toPayTranslated } = useTranslate("à payer");
  const { translatedText: paidTranslated } = useTranslate("payé");
  const { translatedText: bankInfoTitleTranslated } = useTranslate("🏦 Informations pour le virement");
  const { translatedText: accountNumberTranslated } = useTranslate("Numéro de compte :");
  const { translatedText: structuredCommunicationTranslated } = useTranslate("Communication structurée :");
  const { translatedText: registrationConfirmedAfterPaymentTranslated } = useTranslate("⚠️ Votre inscription sera confirmée dès réception du paiement. N'oubliez pas d'indiquer la communication structurée ci-dessus.");
  const { translatedText: emailSentTranslated } = useTranslate("Un email de confirmation a été envoyé à");
  const { translatedText: closeTranslated } = useTranslate("Fermer");
  const { translatedText: eventTitleTranslated } = useTranslate(event?.title || "");
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 text-center">
          {/* Icône de succès ou en attente */}
          <div className="flex justify-center mb-6">
            {isPending ? (
              <Clock 
                className="w-20 h-20" 
                style={{ color: 'rgb(251, 146, 60)' }}
              />
            ) : (
              <CheckCircle 
                className="w-20 h-20" 
                style={{ color: 'rgb(34, 197, 94)' }}
              />
            )}
          </div>

          {/* Message principal */}
          <h2 
            className="text-3xl font-bold mb-4"
            style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
          >
            {isPending ? paymentPendingTranslated : paymentConfirmedTranslated}
          </h2>

          <p 
            className="text-lg text-gray-700 mb-6"
            style={{ fontFamily: '"Lora", serif' }}
          >
            {isPending 
              ? registrationPendingTranslated
              : registrationConfirmedMsgTranslated}
          </p>

          {/* Détails de l'événement */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
            <h3 
              className="font-semibold mb-3 text-lg"
              style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
            >
              <Calendar className="inline w-5 h-5 mr-2" />
              {currentLanguage === 'fr' ? (event?.title || 'Événement') : (eventTitleTranslated || event?.title || eventTranslated)}
            </h3>

            <div className="space-y-2 text-gray-700">
              {registration?.tariffs_details && registration.tariffs_details.length > 0 && (
                <div className="mb-3">
                  <div className="font-medium mb-2" style={{ fontFamily: '"Lora", serif' }}>
                    <Users className="inline w-4 h-4 mr-1" />
                    {ticketsReservedTranslated}
                  </div>
                  {registration.tariffs_details.map((td, idx) => (
                    <div key={idx} className="flex justify-between text-sm ml-5">
                      <span>{td.quantity}x {td.label}</span>
                      <span className="font-semibold">{td.subtotal.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="font-semibold" style={{ fontFamily: '"Lora", serif' }}>
                    <Euro className="inline w-4 h-4 mr-1" />
                    {totalTranslated} {isPending ? toPayTranslated : paidTranslated} :
                  </span>
                  <span 
                    className="text-xl font-bold"
                    style={{ color: 'rgb(196, 150, 96)' }}
                  >
                    {registration?.amount_total?.toFixed(2) || '0.00'} €
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Informations bancaires pour virement */}
          {isPending && bankInfo && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <h4 
                className="font-semibold mb-3"
                style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
              >
                {bankInfoTitleTranslated}
              </h4>
              <div className="space-y-2 text-sm text-gray-700">
                <div>
                  <span className="font-medium">{accountNumberTranslated}</span>
                  <div className="mt-1 p-2 bg-white rounded border font-mono text-xs">
                    {bankInfo}
                  </div>
                </div>
                <div>
                  <span className="font-medium">{structuredCommunicationTranslated}</span>
                  <div className="mt-1 p-2 bg-white rounded border font-mono text-xs">
                    {event?.start_datetime ? event.start_datetime.substring(0, 10).split('-').reverse().join('/') : 'N/A'} - {registration?.buyer_name} - {registration?.buyer_email}
                  </div>
                </div>
                <div className="mt-3 text-xs text-amber-800">
                  {registrationConfirmedAfterPaymentTranslated}
                </div>
              </div>
            </div>
          )}

          {/* Message email */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <Mail className="inline w-5 h-5 mr-2 text-blue-600" />
            <span className="text-blue-800" style={{ fontFamily: '"Lora", serif' }}>
              {emailSentTranslated} <strong>{registration?.buyer_email}</strong>
            </span>
          </div>

          {/* Bouton fermer */}
          <Button
            onClick={onClose}
            className="w-full text-white text-lg py-6"
            style={{ 
              fontFamily: '"Lora", serif',
              backgroundColor: 'rgb(196, 150, 96)'
            }}
          >
            {closeTranslated}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal;
